import { createPortal } from "react-dom";
import { useEffect } from "react";
import { usePopup } from "../../../contexts/PopupContext";
import NewConversationModal from "../../../Center/Chat/input/NewConversationModal";
import {
  Triangle,
  ScreenShare,
  Users,
  MessagesSquare,
  Hash,
  Settings,
} from "lucide-react";
import { usePortalTarget } from "./sidebarUtils";
import useChatSidebarData from "./chat/useChatSidebarData";
import ChatThreadDirectory from "./chat/ChatThreadDirectory";
import ChatTeamDirectory from "./chat/ChatTeamDirectory";
import { useSearchRegistry } from "../../Search/SearchRegistry";

const CHAT_DIRECTORIES = [
  { key: "private", label: "Private", Icon: Triangle, color: "#0EA5E9" },
  { key: "public", label: "Public", Icon: ScreenShare, color: "#912323" },
  { key: "team", label: "Team", Icon: Users, color: "#22C55E" },
];

const ChatSidebarPortal = () => {
  const portalTarget = usePortalTarget("chat-portal-slot");
  const { setIsPopUpOpen, setPopUpComponent } = usePopup();

  const {
    onlineUsers,
    unreadCounts,
    mutedConversations,
    mappedByType,
    teamConversations,
    archivedIds,
    currentThreadId,
    currentTeamId,
    expandedDirs,
    expandedTeamSubs,
    handleSelect,
    handleSelectTeam,
    handleNew,
    toggleDir,
    toggleTeamSub,
    loadTeamConversations,
    loadThreads,
  } = useChatSidebarData();

  const isInNavbar = portalTarget?.dataset?.navbar === "true";

  /* ── Search registry ── */
  const { register, unregister } = useSearchRegistry();
  useEffect(() => {
    // mappedByType items have { id, label, thread_type }
    const privateThreads = (mappedByType.private || []).map((t) => ({
      id: `private-${t.id}`,
      label: t.label,
      keywords: ["private", "thread"],
      icon: Triangle,
      iconColor: "#0EA5E9",
      event: {
        navigateTo: "/chat",
        name: "sidebar:chat-thread-selected",
        detail: { threadId: t.id, thread_type: "private" },
      },
    }));

    const publicThreads = (mappedByType.public || []).map((t) => ({
      id: `public-${t.id}`,
      label: t.label,
      keywords: ["public", "thread"],
      icon: ScreenShare,
      iconColor: "#912323",
      event: {
        navigateTo: "/chat",
        name: "sidebar:chat-thread-selected",
        detail: { threadId: t.id, thread_type: "public" },
      },
    }));

    // teamConversations is { channels: [], groups: [], dms: [] }
    const channels = (teamConversations.channels || []).map((ch) => ({
      id: `team-${ch.team_id}`,
      label: ch.name || "Channel",
      keywords: ["team", "channel", ch.name || ""],
      icon: Hash,
      iconColor: "#22C55E",
      event: {
        navigateTo: "/chat",
        name: "sidebar:chat-thread-selected",
        detail: { threadId: null, thread_type: "team", team_id: ch.team_id },
      },
    }));

    const groups = (teamConversations.groups || []).map((g) => ({
      id: `team-${g.team_id}`,
      label: g.name || "Group",
      keywords: ["team", "group", g.name || ""],
      icon: Users,
      iconColor: "#22C55E",
      event: {
        navigateTo: "/chat",
        name: "sidebar:chat-thread-selected",
        detail: { threadId: null, thread_type: "team", team_id: g.team_id },
      },
    }));

    const dms = (teamConversations.dms || []).map((dm) => ({
      id: `team-${dm.team_id}`,
      label: dm.dm_display_name || "DM",
      keywords: ["team", "dm", "direct message", dm.dm_display_name || ""],
      icon: Users,
      iconColor: "#22C55E",
      event: {
        navigateTo: "/chat",
        name: "sidebar:chat-thread-selected",
        detail: { threadId: null, thread_type: "team", team_id: dm.team_id },
      },
    }));

    const teamItems = [...channels, ...groups, ...dms];

    const items = [
      {
        id: "chat-private",
        label: "Private",
        keywords: ["private", "threads"],
        icon: Triangle,
        iconColor: "#0EA5E9",
        path: "/chat",
        ...(privateThreads.length > 0 ? { children: privateThreads } : {}),
      },
      {
        id: "chat-public",
        label: "Public",
        keywords: ["public", "threads"],
        icon: ScreenShare,
        iconColor: "#912323",
        path: "/chat",
        ...(publicThreads.length > 0 ? { children: publicThreads } : {}),
      },
      {
        id: "chat-team",
        label: "Team",
        keywords: ["team", "channels", "groups", "dms"],
        icon: Users,
        iconColor: "#22C55E",
        path: "/chat",
        ...(teamItems.length > 0 ? { children: teamItems } : {}),
      },
    ];

    register("/chat", items);
    return () => unregister("/chat");
  }, [mappedByType, teamConversations, register, unregister]);

  const content = (
    <div className="flex flex-col gap-y-px">
      {/* ── New-thread buttons (hidden in navbar) ── */}
      {!isInNavbar && (
        <div className="flex items-center gap-x-1.5 px-1.5 py-1">
          {CHAT_DIRECTORIES.map(({ key, label, Icon, color }) => (
            <button
              key={key}
              onClick={() => {
                if (key === "team") {
                  setPopUpComponent(
                    <NewConversationModal
                      onClose={() => setIsPopUpOpen(false)}
                      onConversationCreated={(conv) => {
                        window.dispatchEvent(
                          new CustomEvent("team:join-room", {
                            detail: { team_id: conv.team_id },
                          }),
                        );
                        if (conv.members) {
                          window.dispatchEvent(
                            new CustomEvent("team:invite-members", {
                              detail: {
                                team_id: conv.team_id,
                                members: conv.members,
                              },
                            }),
                          );
                        }
                        loadTeamConversations();
                        handleSelectTeam(conv.team_id);
                      }}
                    />,
                  );
                  setIsPopUpOpen(true);
                } else {
                  handleNew(key);
                }
              }}
              className="group flex-1 flex items-center justify-center rounded-md px-1.5 py-1.5 bg-zinc-100 dark:bg-zinc-800/60 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-all duration-200"
              title={`New ${label.toLowerCase()} thread`}
            >
              <Icon
                className="h-3.5 w-3.5 shrink-0 transition-all duration-200 opacity-60 group-hover:opacity-100 group-hover:scale-110"
                strokeWidth={2}
                style={{ color }}
              />
            </button>
          ))}

          {/* ── Thread manager cog ── */}
          <button
            onClick={() => {
              window.dispatchEvent(new CustomEvent("chat:open-thread-manager"));
              window.dispatchEvent(
                new CustomEvent("sidebar:active-section-changed", {
                  detail: { section: "chat" },
                }),
              );
            }}
            className="group flex items-center justify-center rounded-md px-1.5 py-1.5 bg-zinc-100 dark:bg-zinc-800/60 hover:bg-zinc-50 dark:hover:bg-zinc-800/30 transition-all duration-200"
            title="Manage threads"
          >
            <Settings
              className="h-3.5 w-3.5 shrink-0 transition-all duration-200 opacity-60 group-hover:opacity-100 group-hover:scale-110 text-zinc-500"
              strokeWidth={2}
            />
          </button>
        </div>
      )}

      {/* ── Private & Public directories ── */}
      <ChatThreadDirectory
        dirKey="private"
        label="Private"
        Icon={Triangle}
        color="#0EA5E9"
        threads={mappedByType.private}
        currentThreadId={currentThreadId}
        isExpanded={!!expandedDirs.private}
        onToggle={toggleDir}
        onSelect={handleSelect}
        onReload={loadThreads}
      />
      <ChatThreadDirectory
        dirKey="public"
        label="Public"
        Icon={ScreenShare}
        color="#912323"
        threads={mappedByType.public}
        currentThreadId={currentThreadId}
        isExpanded={!!expandedDirs.public}
        onToggle={toggleDir}
        onSelect={handleSelect}
        onReload={loadThreads}
      />

      {/* ── Team directory ── */}
      <ChatTeamDirectory
        teamConversations={teamConversations}
        archivedIds={archivedIds}
        currentTeamId={currentTeamId}
        unreadCounts={unreadCounts}
        mutedConversations={mutedConversations}
        onlineUsers={onlineUsers}
        isExpanded={!!expandedDirs.team}
        expandedTeamSubs={expandedTeamSubs}
        onToggleDir={toggleDir}
        onToggleSub={toggleTeamSub}
        onSelect={handleSelectTeam}
      />
    </div>
  );

  if (!portalTarget) return null;
  return createPortal(content, portalTarget);
};

export default ChatSidebarPortal;
